import { useState } from "react";
import { makeStyles, MenuItem, useMediaQuery } from "@material-ui/core";
import useTranslation from "next-translate/useTranslation";
import { TheCycleverseTheme } from "src/theme";
import { CalculatorImage } from "../CalculatorImage";
import CalculatorInputSection from "../CalculatorInputSection";
import {
  CalculatorBodyText,
  CalculatorHeadline,
  CalculatorInputHeadline,
  H3Headline,
  InputCaption,
  PreCalculatorHeadline,
} from "../CalculatorTypography";
import { CalculatorButton } from "../components/CalculatorButton";
import { CalculatorButtonLinkCentered } from "../components/CalculatorButtonLinkCentered";
import { InputWithLabelAtTheBottom } from "../components/InputWithLabelAtTheBottom";
import { InputHeader } from "../KalorienverbrauchRechner/InputHeader";
import { TinyInput } from "../TinyInput";
import CalculatorTable from "./CalculatorTable";
import ResusableTableCaculator from "../LumenLuxRechner/CalculatorTable";
import { onlyNumbersRegexDE, onlyNumbersRegexEN } from "src/components/common/constants";
import BarchartGraph from "./BarChart";
import ResultWithColors from "../ResultWithColors";
import { ErrorMessage } from "src/components/common/ErrorMessage";
import { generateDataForGraph, hasUserFilledAllFields } from "./helpers";
import GrayHorizontalLine from "../GrayHorizontalLine";
import LocaleLink from "src/components/Navigation/LocaleLink";
import {
  commonColors,
  dropdown5options1,
  dropdown5options2,
  dropdwon1Options,
  dropdwon3Options,
  fieldsForField4,
  letters,
  listItem,
  tableHeaders,
  tableRows,
} from "./data";
import CalculatorTableMobile from "./CalculatorTableMobile";
import CycleverseDivider from "src/components/CycleverseDivider";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  buttonsContainer: {
    display: "flex",
    [theme.breakpoints.down("xs")]: {
      display: "block",
    },
  },

  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    marginTop: "50px",
  },

  divider: {
    width: "100%",
    height: "1px",
    backgroundColor: "#bababa",
  },
  dividerLabel: {
    width: "40px",
    height: "40px",
    borderRadius: "20px",
    marginTop: "-20.5px",
  },

  dividerContainer: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    alignItems: "center",
    alignContent: "center",
    justifyItems: "center",
    justifyContent: "center",
  },

  buttonMarginTopAndBottom: {
    marginTop: "-27px",
    marginBottom: "-30px",
  },

  centerElement: {
    textAlign: "center",
    marginTop: "50px",
  },

  smallInputsContainer: {
    width: "300px",
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
  },

  marginTop30: {
    marginTop: "30px",
  },

  linkStyles: {
    color: theme?.blueTextColor,
    textDecoration: "underline",
    curson: "pointer",
  },

  captionContainerSpacing: {
    marginTop: "-10px",
    marginBottom: "-40px",
  },

  pointer: {
    cursor: "pointer",
  },

  graphAndTableContainer: {
    width: "100%",
    margin: "auto",
    "@media (max-width: 1079px)": {
      width: "800px",
    },

    "@media (max-width: 832px)": {
      width: "600px",
    },

    "@media (max-width: 621px)": {
      width: "320px",
    },
  },

  graphHeight: {
    maxHeight: "950px",
  },

  graphMediumHeight: {
    maxHeight: "600px",
  },

  graphSmallHeight: {
    maxHeight: "400px",
  },

  centerHeadLine: {
    textAlign: "center",
    marginBottom: "50px",
  },

  checkboxContainer: {
    position: "relative",
    marginTop: "50px",
    fontSize: "medium",
  },

  listContainer: {
    marginTop: 0,
    marginLeft: "-25px",
  },

  fontSizeMedium: {
    fontSize: "medium",
  },

  pinkResultContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  pinkResult: {
    color: theme?.resultColor,
    fontWeight: 900,
    fontSize: "30px",
    marginLeft: "5px",
  },

  paragraphFontSize: {
    fontSize: "medium",
  },

  resultTitle: {
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "5px",
    textAlign: "center",
  },

  alignItems: {
    display: "flex",
    alignItems: "center",
  },
}));

const RitzelCalculatorComponent = () => {
  const { t, lang } = useTranslation("ritzelRechner");
  const classes = useStyles();
  const [fields, setFields] = useState<any>({
    field1: null,
    field2: null,
    field2a: null,
    field2b: null,
    field2c: null,
    field3: null,
    field3a: null,
    field3b: null,
    field4a: null,
    field4b: null,
    field4c: null,
    field4d: null,
    field4e: null,
    field4f: null,
    field4g: null,
    field4h: null,
    field4i: null,
    field4j: null,
    field4k: null,
    field4l: null,
    field4m: null,
    field6a: "0 - 0 - 0",
    field6b: "0 - 0",
    field7: null,
    field8: null,
  });
  const [entriesForField4, setEntriesForField4] = useState<string[]>([]);
  const [resultFields, setResultFields] = useState([]);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [field5ErrorMessage, setField5ErrorMessage] = useState(null);
  const [allFieldRequiredErrorMessage, setAllFieldRequiredErrorMessage] = useState(null);

  const handleCheckBoxClick = () => {
    const resultR = [];
    const resultRx = [];
    for (let i = 1; i < Number(fields?.field3) + 1; i++) {
      resultRx.push(Number(fields[fieldsForField4[Number(i) - 1]]));
      resultR.push(i);
    }

    const { verticalAxisData, colors, greenColorValues, verticalAxisLabelGreen } = generateDataForGraph(
      resultRx,
      fields,
      lang
    );

    setIsChecked(!isChecked);
    setResultFields([
      {
        ...resultFields[0],
        verticalAxisData,
        colors,
        greenColorValues,
        verticalAxisLabelGreen,
      },
    ]);
  };

  const generateFields = (selectedNumber) => {
    const arrayOfFields = [];
    for (let i = 0; i < selectedNumber; i++) {
      arrayOfFields.push(fieldsForField4[i]);
    }

    setFields({
      ...fields,
      field3b: null,
      field3a: null,
      field4a: null,
      field4b: null,
      field4c: null,
      field4d: null,
      field4e: null,
      field4f: null,
      field4g: null,
      field4h: null,
      field4i: null,
      field4j: null,
      field4k: null,
      field4l: null,
      field4m: null,
      field3: selectedNumber,
    });

    setEntriesForField4([...arrayOfFields]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, isTypeNumber?: boolean) => {
    const { name, value } = e.target;
    if (
      isTypeNumber &&
      ((lang === "en" && !onlyNumbersRegexEN.test(value)) || (lang === "de" && !onlyNumbersRegexDE.test(value)))
    ) {
      return false;
    }
    if (name === "field3") {
      setField5ErrorMessage(null);
      generateFields(Number(value));
      return;
    }

    if (name === "field3a") {
      setField5ErrorMessage(null);
      setFields({ ...fields, [fieldsForField4[0]]: value, field3a: value });
    } else if (name === "field3b") {
      setField5ErrorMessage(null);
      setFields({ ...fields, [fieldsForField4[Number(fields?.field3) - 1]]: value, field3b: value });
    } else if (name === "field1") {
      setFields({
        ...fields,
        field6a: "0 - 0 - 0",
        field6b: "0 - 0",
        field2a: null,
        field2b: null,
        field2c: null,
        field1: value,
      });
    } else {
      setFields({ ...fields, [name]: value });
    }
  };

  const generateValuesforField4Enties = () => {
    const { field3, field3a, field3b } = fields;
    if (
      !field3 ||
      field3?.length === 0 ||
      ((!field3a || field3a?.length === 0) && (!field3b || field3b?.length === 0))
    ) {
      setField5ErrorMessage(t("field5ErrorMessage"));
      return;
    }
    const x = (Number(fields?.field3a) / Number(fields?.field3b)) ** (1 / (Number(fields.field3) - 1)) - 1;
    let values = {};
    let currentValue = Number(fields?.field3b);
    for (let i = 1; i < Number(fields?.field3) - 1; i++) {
      currentValue = currentValue * (1 + x);
      values = {
        ...values,
        [fieldsForField4[Number(fields?.field3) - (i + 1)]]: Math.round(currentValue),
      };
    }

    setFields({ ...fields, ...values });
  };

  const calculations = () => {
    const { field1, field2a, field2b, field2c, field3, field4a, field6a, field6b, field7, field8 } = fields;

    setAllFieldRequiredErrorMessage(null);
    const userFilledAllTheField = hasUserFilledAllFields(fields);

    if (!userFilledAllTheField) {
      setAllFieldRequiredErrorMessage(t("allFieldRequiredErrorMessage"));
      return;
    }

    let sumOfField5Value: number;

    if (Number(field1) === 3) {
      const arrayOfDigits = field6a?.split("-");
      sumOfField5Value =
        Number(arrayOfDigits[0].trim()) + Number(arrayOfDigits[1].trim()) + Number(arrayOfDigits[2].trim());
    } else if (Number(field1) === 2) {
      const arrayOfDigits = field6b?.split("-");
      sumOfField5Value = Number(arrayOfDigits[0].trim()) + Number(arrayOfDigits[1].trim());
    } else {
      sumOfField5Value = 0;
    }

    // ** FIRST GROUP OF RESULTS STARTS ** //

    const X = Number(field1) * Number(field3) - sumOfField5Value;

    const Ka = Number(field2a);
    const Kb = Number(field2b);
    const Kc = Number(field2c);

    const Rx1 = Number(field4a);
    const Rxz = Number(fields[fieldsForField4[Number(field3) - 1]]);

    let U1c;
    let U1b;
    let U1a;
    let eMin;
    let Uza;
    let Uzb;
    let Uzc;
    let B;
    let Bpercentage;
    let eMax;
    let Gzc;

    if (Number(field1) === 3) {
      U1c = Kc / Rx1;
      eMin = (U1c * field7) / 1000;
      Uzc = Kc / Rxz;
      Uza = Ka / Rxz;
      B = Uzc / U1c;
      Bpercentage = B * 100;
      eMax = (Uza * field7) / 1000;
      Gzc = (eMax * field8 * 60) / 1000;
    } else if (Number(field1) === 2) {
      U1b = Kb / Rx1;
      eMin = (U1b * field7) / 1000;
      Uzb = Kb / Rxz;
      Uza = Ka / Rxz;
      B = Uzb / U1b;
      Bpercentage = B * 100;
      eMax = (Uza * field7) / 1000;
      Gzc = (eMax * field8 * 60) / 1000;
    } else {
      U1a = Ka / Rx1;
      eMin = (U1a * field7) / 1000;
      Uza = Ka / Rxz;
      B = Uza / U1a;
      Bpercentage = B * 100;
      eMax = (Uza * field7) / 1000;
      Gzc = (eMax * field8 * 60) / 1000;
    }

    // ** FIRST GROUP OF RESULTS ENDS ** //

    // ** PROVIDE DATA FOR TABLE ** //

    let row1 = [];
    let row2 = [];
    let row3 = [];
    let firstCells = [];
    let gResults = [];
    let resultR = [];
    let resultRx = [];

    for (let i = 1; i < field3 + 1; i++) {
      if (!fields[fieldsForField4[Number(i) - 1]] || fields[fieldsForField4[Number(i) - 1]]?.length === 0) {
        setAllFieldRequiredErrorMessage(t("allFieldRequiredErrorMessage"));
        return;
      }
      resultRx.push(Number(fields[fieldsForField4[Number(i) - 1]]));
      resultR.push(i);
    }

    for (let i = 1; i < Number(field1) + 1; i++) {
      firstCells.push([i, Number(fields[`field2${letters[Number(i) - 1]}`])]);
      for (let j = 0; j < Number(field3); j++) {
        const u = Number(fields[`field2${letters[Number(i) - 1]}`]) / Number(fields[fieldsForField4[j]]);

        const e = (u * Number(field7)) / 1000;
        const g = (e * Number(field8) * 60) / 1000;

        gResults.push(g);

        if (i === 1) {
          row1.push([u, e]);
        }

        if (i === 2) {
          row2.push([u, e]);
        }

        if (i === 3) {
          row3.push([u, e]);
        }
      }
    }

    // ** PROVIDING DATA FOR TABLE ENDS ** //

    // ** PROVIDING DATA FOR GRAPH ** //
    const {
      verticalAxisData,
      colors,
      greenColorValues,
      verticalAxisLabel,
      verticalAxisLabelGreen,
    } = generateDataForGraph(resultRx, fields, lang);

    setResultFields([
      {
        R: [...resultR],
        Rx: [...resultRx],
        X,
        B,
        eMin,
        Bpercentage,
        eMax,
        Gzc,
        rows: [{ firstCells: firstCells.reverse() }, { row1: row3 }, { row2: row2 }, { row3: row1 }],
        gResults,
        verticalAxisData,
        verticalAxisLabelGreen,
        colors,
        greenColorValues,
        verticalAxisLabel,
      },
    ]);
  };

  const tableDoesNotFitOnScreen = useMediaQuery("(max-width: 851px)");
  const bigTableDoesNotFitOnScreen = useMediaQuery("(max-width: 1090px)");

  const displayCheckBox = () => {
    return (resultFields[0]?.rows[1]?.row1?.length > 0 || resultFields[0]?.rows[2]?.row2?.length > 0) &&
      ((Number(fields.field1) === 2 && fields.field6b !== "0 - 0") ||
        (Number(fields.field1) === 3 && fields.field6a !== "0 - 0 - 0"))
      ? true
      : false;
  };

  return (
    <div>
      <CalculatorImage
        src="/images/calculators/ritzel-rechner/Ritzelrechner.png"
        layout="intrinsic"
        alt={t("altTextImage1")}
      />
      <CalculatorButton text={t("scrollDownButton")} scroll idToScrollTo="calculator" />

      <CalculatorBodyText>{t("paragraph1")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph2")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph3")}</CalculatorBodyText>

      <div id="calculator" className={classes.centerElement}>
        <CalculatorHeadline>{t("title1")}</CalculatorHeadline>
      </div>

      <CalculatorInputSection
        inputTitle={t("inputTitle1")}
        inputNumber="1"
        isSelect
        name="field1"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
        value={fields?.field1 || ""}
      >
        {dropdwon1Options?.map((el) => (
          <MenuItem key={el} value={el}>
            {t(el)}
          </MenuItem>
        ))}
      </CalculatorInputSection>

      <InputHeader inputNumber="2" inputTitle={t("inputTitle2")}>
        <div className={classes.buttonsContainer}>
          {fields?.field1 === "1" ? (
            <InputWithLabelAtTheBottom
              name="field2a"
              value={fields?.field2a || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
              label={""}
            />
          ) : fields?.field1 === "2" ? (
            <>
              <InputWithLabelAtTheBottom
                name="field2a"
                value={fields?.field2a || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                label={t("input2Label1")}
              />
              <InputWithLabelAtTheBottom
                name="field2b"
                value={fields?.field2b || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                label={t("input2Label3")}
              />
            </>
          ) : (
            <>
              <InputWithLabelAtTheBottom
                name="field2a"
                value={fields?.field2a || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                label={t("input2Label1")}
              />
              <InputWithLabelAtTheBottom
                name="field2b"
                value={fields?.field2b || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                label={t("input2Label2")}
              />
              <InputWithLabelAtTheBottom
                name="field2c"
                value={fields?.field2c || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                label={t("input2Label3")}
              />
            </>
          )}
        </div>
      </InputHeader>

      <InputHeader inputNumber="3" inputTitle={""}>
        <div style={{ marginTop: "-65px", marginBottom: "35px" }}>
          <CalculatorInputSection
            inputTitle={t("inputTitle8")}
            isNotSection
            isSelect
            name="field3"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
            value={fields?.field3 || ""}
          >
            {dropdwon3Options?.map((el) => (
              <MenuItem key={el} value={el}>
                {t(el)}
              </MenuItem>
            ))}
          </CalculatorInputSection>
        </div>

        {Number(fields?.field3) === 1 ? (
          <InputWithLabelAtTheBottom
            name="field3a"
            value={fields?.field3a || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
            label={t("input3Label1a")}
          />
        ) : (
          <div className={classes.buttonsContainer}>
            <InputWithLabelAtTheBottom
              name="field3a"
              value={fields?.field3a || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
              label={t("input3Label1")}
            />
            <InputWithLabelAtTheBottom
              name="field3b"
              value={fields?.field3b || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
              label={t("input3Label2")}
            />
          </div>
        )}
      </InputHeader>

      {Number(fields?.field3 > 2) ? (
        <div className={`${classes.container}`}>
          <div className={classes.dividerContainer}>
            <div className={classes.divider}></div>
            <img className={classes.dividerLabel} src={`/images/calculators/number_icons/orange_4.svg`} alt="number" />
          </div>
          <CalculatorInputHeadline>{t("inputTitle4")}</CalculatorInputHeadline>
          <div style={{ marginTop: "-50px", marginBottom: "-30px" }}>
            <CalculatorBodyText>{t("input4Caption")}</CalculatorBodyText>
          </div>

          {field5ErrorMessage ? (
            <div className={classes.marginTop30}>
              <ErrorMessage message={field5ErrorMessage} />
            </div>
          ) : null}

          <CalculatorButton text={t("input4Button")} onClick={generateValuesforField4Enties} />
          <div style={{ marginTop: "-15px" }}>
            <InputCaption>{t("input4Caption2")}</InputCaption>
          </div>

          <div className={classes.smallInputsContainer}>
            {entriesForField4 && entriesForField4.length ? (
              entriesForField4.map((entry) => (
                <TinyInput
                  key={entry}
                  name={entry}
                  value={fields[entry] || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
                />
              ))
            ) : (
              <>
                {fieldsForField4.map((field) => (
                  <TinyInput
                    key={field}
                    name={field}
                    value={fields[field] || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      ) : null}

      {fields?.field1 && Number(fields?.field1) === 3 ? (
        <CalculatorInputSection
          inputTitle={t("inputTitle5")}
          inputNumber={Number(fields?.field3 > 2) ? "5" : "4"}
          isSelect
          name="field6a"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
          value={fields?.field6a || ""}
          inputCaption={
            <LocaleLink href={t("input5Link")} as="" anchor>
              {t("input5Caption")}
            </LocaleLink>
          }
        >
          {dropdown5options1?.map((el) => (
            <MenuItem key={el} value={el}>
              {el}
            </MenuItem>
          ))}
        </CalculatorInputSection>
      ) : fields?.field1 && Number(fields?.field1) === 2 ? (
        <CalculatorInputSection
          inputTitle={t("inputTitle5")}
          inputNumber={Number(fields?.field3 > 2) ? "5" : "4"}
          isSelect
          name="field6b"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
          value={fields?.field6b || ""}
          inputCaption={
            <LocaleLink href={t("input5Link")} as="" anchor>
              {t("input5Caption")}
            </LocaleLink>
          }
        >
          {dropdown5options2?.map((el) => (
            <MenuItem key={el} value={el}>
              {el}
            </MenuItem>
          ))}
        </CalculatorInputSection>
      ) : null}

      <CalculatorInputSection
        inputTitle={t("inputTitle6")}
        inputNumber={
          Number(fields?.field1) > 1 && Number(fields?.field3 > 2)
            ? "6"
            : Number(fields?.field1) > 1 && Number(fields?.field3 <= 2)
            ? "5"
            : "4"
        }
        name="field7"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
        value={fields?.field7 || ""}
        inputUnite={"mm"}
        inputCaption={
          <span className={`${classes.captionContainerSpacing}`}>
            <span>{t("input6Caption1")}</span>
            <LocaleLink href={`/rechner/reifenumfang-rechner`} as={`/rechner/reifenumfang-rechner`} target="new">
              {t("input6Caption2")}
            </LocaleLink>
          </span>
        }
      />

      <InputHeader
        inputNumber={
          Number(fields?.field1) > 1 && Number(fields?.field3 > 2)
            ? "7"
            : Number(fields?.field1) > 1 && Number(fields?.field3 <= 2)
            ? "6"
            : "5"
        }
        inputTitle={t("inputTitle7")}
      >
        <InputWithLabelAtTheBottom
          name="field8"
          value={fields?.field8 || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
          label={t("input7label")}
          isNotSmall
        />
      </InputHeader>

      {allFieldRequiredErrorMessage ? (
        <div className={classes.marginTop30}>
          <ErrorMessage message={allFieldRequiredErrorMessage} />
        </div>
      ) : null}

      <CalculatorButton onClick={calculations} text={t("calculateButton")} />

      {resultFields[0]?.Bpercentage && resultFields[0]?.Gzc && resultFields[0]?.X ? (
        <>
          <div className={classes.resultTitle}>{`${t("resultTitle2")}:`}</div>
          <ResultWithColors
            results={[
              {
                value: `${resultFields[0]?.B.toLocaleString(lang, { maximumFractionDigits: 1 })} ${t(
                  "or"
                )} ${resultFields[0]?.Bpercentage.toLocaleString(lang, { maximumFractionDigits: 1 })} %`,
                color: commonColors["blue"],
                title: t("firstResult"),
              },
              {
                value: `${resultFields[0]?.Gzc.toLocaleString(lang, { maximumFractionDigits: 1 })} km/h`,
                color: commonColors["red"],
                title: t("secondResult"),
              },
              {
                value: resultFields[0]?.X,
                color: commonColors["green"],
                title: t("thirdResult"),
              },
            ]}
          />
        </>
      ) : null}

      {resultFields[0]?.eMin && (
        <div className={classes.pinkResultContainer}>
          <InputCaption>{t("fourResult")}</InputCaption>
          {Number(fields?.field1) === 1 && Number(fields?.field3) === 1 ? (
            <div className={classes.alignItems}>
              <span className={classes.pinkResult}>
                {resultFields[0]?.eMin.toLocaleString(lang, { maximumFractionDigits: 2 })}
              </span>
            </div>
          ) : (
            <div>
              <span className={classes.fontSizeMedium}>{t("min")}:</span>
              <span className={classes.pinkResult}>
                {resultFields[0]?.eMin.toLocaleString(lang, { maximumFractionDigits: 2 })}
              </span>
              <span style={{ fontSize: "medium", marginLeft: "10px" }}>{t("max")}:</span>
              <span className={classes.pinkResult}>
                {resultFields[0]?.eMax.toLocaleString(lang, { maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: "40px" }}>
        <GrayHorizontalLine />
      </div>

      {resultFields && resultFields[0]?.rows?.length ? (
        (bigTableDoesNotFitOnScreen && Number(fields?.field3) > 9) || tableDoesNotFitOnScreen ? (
          <CalculatorTableMobile
            field1={fields?.field1}
            results={resultFields}
            field5={Number(fields?.field1) === 3 ? fields?.field6a : fields?.field6b}
            display={() => displayCheckBox()}
          />
        ) : (
          <CalculatorTable
            field1={fields?.field1}
            results={resultFields}
            field5={Number(fields?.field1) === 3 ? fields?.field6a : fields?.field6b}
            display={() => displayCheckBox()}
          />
        )
      ) : null}

      {resultFields && resultFields[0]?.rows?.length ? <GrayHorizontalLine /> : null}

      {resultFields[0]?.verticalAxisData ? (
        <div
          className={
            fields?.field3 && Number(fields?.field3) > 9
              ? `${classes.graphAndTableContainer} ${classes.graphHeight}`
              : fields?.field3 && Number(fields?.field3) <= 9 && fields?.field3 && Number(fields?.field3) > 6
              ? `${classes.graphAndTableContainer} ${classes.graphMediumHeight}`
              : `${classes.graphAndTableContainer} ${classes.graphSmallHeight}`
          }
        >
          <div className={`${classes.centerElement}`}>
            <H3Headline>{t("graphTitle")}</H3Headline>
          </div>

          <BarchartGraph graphData={resultFields[0]} isChecked={isChecked} lang={lang} />
        </div>
      ) : null}

      {displayCheckBox() ? (
        <div className={classes.checkboxContainer}>
          <input type="checkbox" id="check" name="check" checked={isChecked} onChange={handleCheckBoxClick} />
          {t("checkBoxText")}
        </div>
      ) : null}

      {resultFields[0]?.verticalAxisData ? (
        <div style={{ marginTop: "60px" }}>
          <CycleverseDivider />
        </div>
      ) : null}

      <div style={{ textAlign: "center", marginTop: "30px" }}>
        <CalculatorHeadline>{t("title2")}</CalculatorHeadline>
      </div>

      <H3Headline>{t("paragraphTitle1")}</H3Headline>
      <CalculatorBodyText>{t("paragraph4")}</CalculatorBodyText>

      <H3Headline>{t("paragraphTitle2")}</H3Headline>
      <CalculatorBodyText>{t("paragraph5")}</CalculatorBodyText>

      <H3Headline>{t("paragraphTitle3")}</H3Headline>
      <CalculatorBodyText>{t("paragraph6")}</CalculatorBodyText>

      <H3Headline>{t("paragraphTitle4")}</H3Headline>
      <CalculatorBodyText>{t("paragraph7")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph8")}</CalculatorBodyText>

      <H3Headline>{t("paragraphTitle5")}</H3Headline>
      <CalculatorBodyText>{t("paragraph9")}</CalculatorBodyText>

      <div id={t("input5Link")}>
        <H3Headline>{t("paragraphTitle6")}</H3Headline>
      </div>
      <CalculatorBodyText>{t("paragraph10")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph11")}</CalculatorBodyText>

      <ul>
        {listItem.map((item) => (
          <li key={item}>{<CalculatorBodyText>{t(`${item}`)}</CalculatorBodyText>}</li>
        ))}
      </ul>

      <div style={{ fontSize: "medium" }}>
        <CalculatorBodyText>{t("paragraph12")}</CalculatorBodyText>
      </div>

      <CalculatorBodyText>{t("paragraph13")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph14")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph15")}</CalculatorBodyText>

      <ResusableTableCaculator headers={tableHeaders} rows={tableRows} centered />

      <p className={classes.fontSizeMedium}>
        <span style={{ fontWeight: "bold" }}>{t("paragraphFirstPart")}</span>
        <span>{t("paragraph16")}</span>
        <LocaleLink
          href={`/blog/gangschaltung-fahrrad-leitfaden`}
          as={`/blog/gangschaltung-fahrrad-leitfaden`}
          target="new"
        >
          {t("lastPartLink")}
        </LocaleLink>
      </p>

      <PreCalculatorHeadline>{t("title3")}</PreCalculatorHeadline>
      <div className={classes.buttonMarginTopAndBottom}>
        <CalculatorButtonLinkCentered text={t("buttonText1")} href="/antrieb" />
      </div>

      <PreCalculatorHeadline>{t("title4")}</PreCalculatorHeadline>
      <div className={classes.buttonMarginTopAndBottom}>
        <CalculatorButtonLinkCentered text={t("buttonText2")} href="/schaltung" />
      </div>
    </div>
  );
};
export default RitzelCalculatorComponent;
